import { Editor } from "@monaco-editor/react";
import { constrainedEditor } from "constrained-editor-plugin";
import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { editorConfigs } from "./editorConfigs";
import CodeEditorMenu from "./CodeEditorMenu";
import type { SourceFormat } from "../types/editor";
import "./CodeEditor.css";

interface CodeEditorProps {
  /** The current source content to display. */
  content: string;
  /** Determines the Monaco language mode (`"xml"` for PreTeXt, `"latex"` for LaTeX). */
  sourceFormat: SourceFormat;
  /** Called (debounced 500 ms) whenever the user edits the content. */
  onChange: (value: string | undefined) => void;
  /** If provided, Ctrl+Enter in the editor triggers this callback. */
  onRebuild?: () => void;
  /** If provided, Ctrl+S in the editor triggers this callback. */
  onSave?: () => void;
  /** Called when the user clicks "Import LaTeX" in the toolbar. */
  onOpenLatexImport: () => void;
  /** Called when the user clicks "Edit Macros" in the toolbar. */
  onOpenDocinfoEditor: () => void;
  /**
   * If provided, a "Convert to PreTeXt" button is shown in the toolbar.
   * Called when the user clicks to open the conversion confirmation dialog.
   */
  onOpenConvertToPretext?: () => void;
  /**
   * Controls whether the "Convert to PreTeXt" button is enabled.
   * Should be `false` when conversion has failed.
   */
  canConvertToPretext?: boolean;
  /** If provided, an "Assets" button is shown in the toolbar (PreTeXt mode only). */
  onOpenAssets?: () => void;
  /** Called when the user clicks "Display Full Source" to open the assembled-source modal. */
  onShowFullSource: () => void;
  /**
   * Called when the user clicks any locked leading line of the wrapper (the
   * opening tag, and the title line right after it when present). Hosts use
   * this to open the division's properties editor in the Table of Contents,
   * since the tag/title/xml:id aren't editable in-place.
   */
  onRequestWrapperEdit?: () => void;
}

/** Imperative handle exposed via `forwardRef` for programmatic control. */
export interface CodeEditorHandle {
  /** Insert `text` at the current cursor position (or replace the selection). */
  insertAtCursor: (text: string) => void;
}

/**
 * Returns the line number where a PreTeXt division's locked header ends. The
 * header is always at least the opening tag (line 1); if a `<title>` element
 * immediately follows it on line 2, the header is extended through that
 * element's closing `</title>` line, since titles are now only editable from
 * the TOC. Divisions with no title line (introduction/conclusion) keep a
 * one-line header.
 */
function findPretextHeaderEnd(model: any): number {
  const lineCount = model.getLineCount();
  if (lineCount < 2 || !/^\s*<title\b/.test(model.getLineContent(2))) return 1;
  let end = 2;
  while (end <= lineCount && !/<\/title\s*>/.test(model.getLineContent(end))) {
    end++;
  }
  return end <= lineCount ? end : 1;
}

/**
 * Describes which lines of the model are structural (locked) for a given source
 * format.  `editableRange` is the single multiline region the user may edit
 * (Monaco `[startLine, startCol, endLine, endCol]`); every other line is
 * locked.  `leadingLockedLines` is how many lines at the very top are locked —
 * clicking any of them opens the division's properties form in the TOC.
 * Returns `null` when nothing should be locked (e.g. mid-edit or unsupported
 * format), leaving the whole document editable.
 */
function computeLockedRegion(
  model: any,
  sourceFormat: SourceFormat,
): {
  editableRange: [number, number, number, number];
  lockedLines: number[];
  leadingLockedLines: number;
} | null {
  const lineCount = model.getLineCount();

  // PreTeXt: lock the opening tag (plus the title line right after it, when
  // present) and the closing tag, keeping the body in between editable.
  if (sourceFormat === "pretext") {
    if (lineCount < 3) return null;
    const headerEnd = findPretextHeaderEnd(model);
    if (headerEnd >= lineCount - 1) return null;
    const lockedLines: number[] = [];
    for (let ln = 1; ln <= headerEnd; ln++) lockedLines.push(ln);
    lockedLines.push(lineCount);
    return {
      editableRange: [
        headerEnd + 1,
        1,
        lineCount - 1,
        model.getLineMaxColumn(lineCount - 1),
      ],
      lockedLines,
      leadingLockedLines: headerEnd,
    };
  }

  // Markdown: lock a leading `---` ... `---` YAML frontmatter block (the
  // division's type/xml:id/label) and the title's `# heading` right after it,
  // keeping the markdown body below editable.
  if (sourceFormat === "markdown") {
    if (model.getLineContent(1).trim() !== "---") return null;
    let fence = -1;
    for (let ln = 2; ln <= lineCount; ln++) {
      if (model.getLineContent(ln).trim() === "---") {
        fence = ln;
        break;
      }
    }
    if (fence === -1) return null;

    // The title lives in the body as the leading `# heading`, possibly after
    // some blank spacing lines; lock through it too, since it's now only
    // editable from the TOC. Stop at the first non-blank line that isn't a
    // heading — that's the body, and is left untouched.
    let headerEnd = fence;
    let scan = fence + 1;
    while (scan <= lineCount && model.getLineContent(scan).trim() === "") scan++;
    if (scan <= lineCount && /^#{1,6}[ \t]+\S/.test(model.getLineContent(scan))) {
      headerEnd = scan;
    }

    // Need at least one body line after the locked header; otherwise lock
    // nothing (don't trap the user mid-edit).
    if (headerEnd >= lineCount) return null;
    const lockedLines: number[] = [];
    for (let ln = 1; ln <= headerEnd; ln++) lockedLines.push(ln);
    return {
      editableRange: [headerEnd + 1, 1, lineCount, model.getLineMaxColumn(lineCount)],
      lockedLines,
      leadingLockedLines: headerEnd,
    };
  }

  // LaTeX: a `\section{title}\label{ref}` header (or `\worksheet{…}` etc. — the
  // command is named after the division type) occupies the first line; lock it
  // and keep the body below editable, so the type/title/xml:id are edited from
  // the TOC instead. Other LaTeX divisions (introduction/conclusion comments,
  // `\begin{section}` environments, a multi-section document root that opens
  // with prose) have no single header line to freeze, so nothing is locked.
  if (sourceFormat === "latex") {
    // Need a body line after the header; otherwise locking line 1 would leave
    // no editable region and trap the user.
    if (lineCount < 2) return null;
    if (
      !/^\s*\\(?!begin\b|end\b)[A-Za-z][A-Za-z-]*\*?\{/.test(
        model.getLineContent(1),
      )
    )
      return null;
    return {
      editableRange: [2, 1, lineCount, model.getLineMaxColumn(lineCount)],
      lockedLines: [1],
      leadingLockedLines: 1,
    };
  }

  return null;
}

/** Static Monaco editor options shared across all instances of this component. */
const options = {
  automaticLayout: true,
  minimap: { enabled: false },
  acceptSuggestionOnCommitCharacter: false,
  quickSuggestions: false,
  wordWrap: "on" as const,
  insertSpaces: true,
  tabSize: 2,
  padding: { top: 10, bottom: 10 },
};

/**
 * Monaco-based code editor with an attached toolbar.
 *
 * Manages its own undo/redo state so the toolbar buttons stay in sync with
 * the editor model.  `onRebuild` and `onSave` callbacks are stored in refs
 * so keyboard shortcuts registered at mount time always call the latest
 * version without needing to re-register.
 *
 * Content is synced from props only when the prop value differs from what the
 * editor model already contains, to prevent cursor jumps on re-render.
 *
 * Exposes a {@link CodeEditorHandle} via `forwardRef` for programmatic control.
 */
const CodeEditor = forwardRef<CodeEditorHandle, CodeEditorProps>(({
  content,
  sourceFormat,
  onChange,
  onRebuild,
  onSave,
  onOpenLatexImport,
  onOpenDocinfoEditor,
  onOpenConvertToPretext,
  canConvertToPretext,
  onOpenAssets,
  onShowFullSource,
  onRequestWrapperEdit,
}, ref) => {
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const constrainedRef = useRef<ReturnType<typeof constrainedEditor> | null>(null);
  const lockedDecorationsRef = useRef<any>(null);
  const lockedRef = useRef(false);
  // How many lines at the very top are locked (the wrapper tag + title for
  // PreTeXt, the frontmatter block + `# heading` for Markdown). Clicking any
  // of them opens the TOC properties form.
  const leadingLockedLinesRef = useRef(0);
  const contentListenerRef = useRef<{ dispose: () => void } | null>(null);
  const mouseListenerRef = useRef<{ dispose: () => void } | null>(null);
  const onRequestWrapperEditRef = useRef(onRequestWrapperEdit);
  const completionProviderRef = useRef<{ dispose: () => void } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProgrammaticUpdateRef = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const onRebuildRef = useRef(onRebuild);
  const onSaveRef = useRef(onSave);

  useImperativeHandle(ref, () => ({
    insertAtCursor: (text: string) => {
      const editor = editorRef.current;
      if (!editor) return;
      const selection = editor.getSelection();
      if (!selection) return;
      editor.executeEdits("insert-asset", [
        { range: selection, text, forceMoveMarkers: true },
      ]);
      editor.focus();
    },
  }), []);

  useEffect(() => {
    onRebuildRef.current = onRebuild;
  }, [onRebuild]);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    onRequestWrapperEditRef.current = onRequestWrapperEdit;
  }, [onRequestWrapperEdit]);
  // const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    completionProviderRef.current?.dispose?.();
    const config = editorConfigs[sourceFormat];
    completionProviderRef.current =
      monacoRef.current
        ? (config.registerMonacoExtensions?.(monacoRef.current) ?? null)
        : null;
    // Switching format toggles whether the wrapper is locked (PreTeXt only).
    applyConstraints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFormat]);

  useEffect(() => {
    return () => {
      contentListenerRef.current?.dispose?.();
      completionProviderRef.current?.dispose?.();
      mouseListenerRef.current?.dispose?.();
      constrainedRef.current?.disposeConstrainer?.();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // When a PreTeXt division has collapsed to exactly its locked header
  // (opening tag, plus a title line when present) directly above its closing
  // tag, insert a blank line between them so the body always has at least one
  // editable, non-locked line. The edit is flagged programmatic so it doesn't
  // echo back to the host as a content change (it's a display normalization,
  // not a user edit).
  const ensurePretextBodyLine = (editor: any, model: any, monaco: any) => {
    if (sourceFormat !== "pretext") return;
    const lineCount = model.getLineCount();
    if (lineCount < 2) return;
    const open = model.getLineContent(1).trim();
    if (!open.startsWith("<") || open.startsWith("</") || open.endsWith("/>")) {
      return;
    }
    const headerEnd = findPretextHeaderEnd(model);
    if (lineCount !== headerEnd + 1) return;
    const close = model.getLineContent(lineCount).trim();
    if (!/^<\/[A-Za-z][\w.:-]*\s*>$/.test(close)) return;

    isProgrammaticUpdateRef.current = true;
    const endCol = model.getLineMaxColumn(headerEnd);
    editor.executeEdits("ensure-body-line", [
      {
        range: new monaco.Range(headerEnd, endCol, headerEnd, endCol),
        text: "\n",
        forceMoveMarkers: true,
      },
    ]);
    queueMicrotask(() => {
      isProgrammaticUpdateRef.current = false;
    });
  };

  // An imported PreTeXt division can end with one or more trailing blank
  // lines after its closing tag (e.g. carried over from a file that ended
  // with a newline). `computeLockedRegion` always locks the model's last
  // line as the closing tag, so a trailing blank line leaves the real
  // closing tag sitting inside the "editable" region, unprotected. Strip
  // any such trailing blank lines so the closing tag is truly last and gets
  // locked correctly.
  const trimPretextTrailingBlankLines = (editor: any, model: any, monaco: any) => {
    if (sourceFormat !== "pretext") return;
    const lineCount = model.getLineCount();
    if (lineCount < 2 || model.getLineContent(lineCount).trim() !== "") return;
    let lastContentLine = lineCount - 1;
    while (lastContentLine > 1 && model.getLineContent(lastContentLine).trim() === "") {
      lastContentLine--;
    }
    const closing = model.getLineContent(lastContentLine).trim();
    if (!/^<\/[A-Za-z][\w.:-]*\s*>$/.test(closing)) return;

    isProgrammaticUpdateRef.current = true;
    editor.executeEdits("trim-trailing-blank-lines", [
      {
        range: new monaco.Range(
          lastContentLine,
          model.getLineMaxColumn(lastContentLine),
          lineCount,
          model.getLineMaxColumn(lineCount),
        ),
        text: "",
        forceMoveMarkers: true,
      },
    ]);
    queueMicrotask(() => {
      isProgrammaticUpdateRef.current = false;
    });
  };

  // Lock the division's structural metadata so it can't be edited in the code
  // editor: the body stays editable, but the type/xml:id/label are edited from
  // the Table of Contents instead. For PreTeXt this is the wrapper tag on the
  // first/last lines; for Markdown it's the leading YAML frontmatter block.
  // Recomputed whenever the content or source format changes; see
  // {@link computeLockedRegion} for the per-format geometry.
  const applyConstraints = () => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    const instance = constrainedRef.current;
    if (!editor || !monaco || !instance) return;
    const model = editor.getModel();
    if (!model) return;

    // Start clean — the previous restrictions reference stale line numbers.
    if (typeof model.disposeRestrictions === "function") {
      model.disposeRestrictions();
    }

    trimPretextTrailingBlankLines(editor, model, monaco);

    // A PreTeXt division whose body is emptied collapses to just its locked
    // wrapper tags (`<section…>` / `</section>`) on adjacent lines. With no line
    // between them there's nowhere unlocked to type — and `computeLockedRegion`
    // bails (`lineCount < 3`), dropping the wrapper protection entirely. Insert a
    // blank middle line so the wrapper stays locked and there is always a clean,
    // editable line in between to add content back into.
    ensurePretextBodyLine(editor, model, monaco);

    const region = computeLockedRegion(model, sourceFormat);
    lockedRef.current = region !== null;
    leadingLockedLinesRef.current = region?.leadingLockedLines ?? 0;

    if (region) {
      instance.addRestrictionsTo(model, [
        { range: region.editableRange, allowMultiline: true },
      ]);
    }

    // Cosmetic: dim the locked lines so they read as structural.
    if (typeof editor.createDecorationsCollection === "function") {
      if (!lockedDecorationsRef.current) {
        lockedDecorationsRef.current = editor.createDecorationsCollection();
      }
      if (!region) {
        lockedDecorationsRef.current.clear();
      } else {
        const hoverMessage = {
          value:
            "These properties are structural — edit them from the Table of Contents.",
        };
        // `className` + `isWholeLine` paints a full-width tint *behind* the text;
        // `inlineClassName` is merged onto the token spans themselves so we can
        // recolor them. The latter only applies to the decoration's range, so
        // each range must span the whole line's text (col 1 → last column).
        const lockedLineOptions = {
          isWholeLine: true,
          className: "pretext-plus-editor__locked-line",
          inlineClassName: "pretext-plus-editor__locked-line-text",
          // Never let the gray inline style grow onto text typed at a locked
          // line's edges — otherwise content re-added next to the wrapper tags
          // inherits the structural styling and looks (and reads) as locked.
          stickiness:
            monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
          hoverMessage,
        };
        lockedDecorationsRef.current.set(
          region.lockedLines.map((ln) => ({
            range: new monaco.Range(ln, 1, ln, model.getLineMaxColumn(ln)),
            options: lockedLineOptions,
          })),
        );
      }
    }
  };

  const setModelValueSafely = (model: any, nextValue: string) => {
    if (model.getValue() === nextValue) return;
    isProgrammaticUpdateRef.current = true;
    // Restrictions must be removed before a programmatic full-document setValue,
    // or the plugin reverts it as an edit to the locked ranges. They're
    // re-applied (against the new line count) immediately after.
    if (typeof model.disposeRestrictions === "function") {
      model.disposeRestrictions();
    }
    model.setValue(nextValue);
    applyConstraints();
    queueMicrotask(() => {
      isProgrammaticUpdateRef.current = false;
    });
  };

  // When the content prop changes from an external source, update the editor
  // model only if it actually differs from what the editor currently contains.
  // This prevents cursor jumps caused by the parent re-rendering with the same
  // value the user just typed.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const model = editor.getModel();
    if (!model) return;
    if (model.getValue() !== content) {
      const position = editor.getPosition();
      const selections = editor.getSelections();
      setModelValueSafely(model, content);
      if (position) editor.setPosition(position);
      if (selections) editor.setSelections(selections);
    }
  }, [content]);

  const handleEditorMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    // Wire up the constrained-editor instance once per mount; restrictions
    // themselves are (re)applied by applyConstraints below and on every sync.
    constrainedRef.current = constrainedEditor(monaco);
    constrainedRef.current.initializeIn(editor);
    // Ensure the newly mounted editor has the latest content in case the
    // component was remounted while content changed without triggering the
    // content-sync effect (editorRef.current was null at that point).
    const model = editor.getModel();
    if (model && model.getValue() !== content) {
      setModelValueSafely(model, content);
    }
    // Subscribe to content changes to refresh undo/redo availability
    contentListenerRef.current?.dispose?.();
    contentListenerRef.current = editor.onDidChangeModelContent(() => {
      updateUndoRedoState();
    });
    updateUndoRedoState();

    // Clicking any locked leading line (the PreTeXt opening tag/title, or the
    // Markdown frontmatter block + title heading) opens the division's
    // properties editor in the TOC, since the type/title/xml:id/label can't
    // be edited in place.
    mouseListenerRef.current?.dispose?.();
    mouseListenerRef.current = editor.onMouseDown((e: any) => {
      const line = e?.target?.position?.lineNumber;
      if (
        lockedRef.current &&
        typeof line === "number" &&
        line <= leadingLockedLinesRef.current
      ) {
        onRequestWrapperEditRef.current?.();
      }
    });

    // Register Ctrl+Enter to trigger a full rebuild
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
      onRebuildRef.current?.();
    });

    // Register Ctrl+S to save (and rebuild if a rebuild handler is set)
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      onSaveRef.current?.();
    });

    completionProviderRef.current?.dispose?.();
    const config = editorConfigs[sourceFormat];
    completionProviderRef.current = config.registerMonacoExtensions?.(monaco) ?? null;

    applyConstraints();
  };

  /**
   * Reads the Monaco model's undo/redo availability and updates state.
   * Falls back to enabling both buttons whenever an editor instance exists,
   * because some Monaco builds don't expose `canUndo`/`canRedo` on the model.
   */
  const updateUndoRedoState = () => {
    const model = editorRef.current?.getModel?.();
    if (
      model &&
      typeof (model as any).canUndo === "function" &&
      typeof (model as any).canRedo === "function"
    ) {
      try {
        setCanUndo((model as any).canUndo());
        setCanRedo((model as any).canRedo());
        return;
      } catch {
        // fall through to default
      }
    }
    // Fallback: enable buttons when editor exists
    const hasEditor = !!editorRef.current;
    setCanUndo(hasEditor);
    setCanRedo(hasEditor);
  };

  const handleContentChange = (newContent: string) => {
    onChange(newContent);
  };

  const handleUndo = () => {
    editorRef.current?.trigger("", "undo");
    updateUndoRedoState();
  };

  const handleRedo = () => {
    editorRef.current?.trigger("", "redo");
    updateUndoRedoState();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CodeEditorMenu
        content={content}
        sourceFormat={sourceFormat}
        onContentChange={handleContentChange}
        onOpenLatexImport={onOpenLatexImport}
        onOpenDocinfoEditor={onOpenDocinfoEditor}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        onConvertToPretext={onOpenConvertToPretext}
        canConvertToPretext={canConvertToPretext}
        onOpenAssets={onOpenAssets}
        onShowFullSource={onShowFullSource}
      />
      <div style={{ flex: 1 }}>
        <Editor
          options={options}
          height="100%"
          language={editorConfigs[sourceFormat].language}
          defaultValue={content}
          onMount={handleEditorMount}
          onChange={(value, event) => {
            if (event?.isFlush || isProgrammaticUpdateRef.current) {
              return;
            }
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
              handleContentChange(value || "");
            }, 500);
          }}
        />
      </div>
    </div>
  );
});

CodeEditor.displayName = "CodeEditor";

export default CodeEditor;
